import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../environment/environment';
import {
  LoginUserModel,
  PasswordResetModel,
  RegisterUserModel,
  UserBackendModel,
} from '../models/user.model';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public authCheck$ = new BehaviorSubject<boolean>(null);

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {}

  // Fetch user data if session is active
  async checkSession() {
    const formData = new FormData();
    formData.append('mode', 'getCurrentUser');

    try {
      await lastValueFrom(
        this.http
          .post<{
            success: boolean;
            message: string;
            data: UserBackendModel;
          }>(environment.authority_short + 'user.php', formData)
          .pipe(
            tap(user => {
              this.authCheck$.next(true);
              const currentUser = this.userService.parseBackendUser(user.data);
              this.userService.setUser(currentUser);
              this.userService.currentUser$.next(currentUser);
            }),
            catchError(error => {
              this.authCheck$.next(false);
              // prevent error from propagating
              return of(null);
            })
          )
      );
    } catch (error) {
      console.error('Session check failed:', error);
    }
  }

  isAuthenticated(): Observable<boolean> {
    const formData = new FormData();
    formData.append('mode', 'getCurrentUser');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: UserBackendModel;
      }>(environment.authority_short + 'user.php', formData)
      .pipe(
        map(response => {
          console.log('isAuthenticated response', response);
          return true;
        }),
        catchError(error => {
          return of(false);
        })
      );
  }

  public async register(user: RegisterUserModel): Promise<void> {
    const formData = new FormData();
    formData.append('mode', 'register');
    formData.append('first_name', user.firstname);
    formData.append('last_name', user.lastname);
    formData.append('email', user.email);
    formData.append('password', user.password);
    if (user.newsletterOptIn) {
      formData.append('newsletter_opt_in', '1');
    }

    try {
      await firstValueFrom(
        this.http.post(environment.authority_short + 'auth.php', formData).pipe(
          catchError(error => {
            return throwError(() => new Error('Registration failed'));
          })
        )
      );
      // Handle success
    } catch (error) {
      // Handle error
      throw error;
    }
  }

  public async login(user: LoginUserModel): Promise<UserBackendModel> {
    const formData = new FormData();
    formData.append('mode', 'login');
    formData.append('email', user.email);
    formData.append('password', user.password);

    try {
      const response = await firstValueFrom(
        this.http
          .post<{
            data: UserBackendModel;
            message: string;
            success: boolean;
          }>(environment.authority_short + 'auth.php', formData)
          .pipe(
            catchError(error => {
              if (error.status === 401) {
                return throwError(() => new Error('Invalid credentials'));
              }
              if (error.status === 409) {
                return throwError(() => new Error('User not verified'));
              }
              return throwError(() => new Error('Login failed'));
            })
          )
      );
      const currentUser = this.userService.parseBackendUser(response.data);
      this.userService.setUser(currentUser);
      this.userService.currentUser$.next(currentUser);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async verifyEmail(token: string): Promise<void> {
    const formData = new FormData();
    formData.append('mode', 'verifyMail');
    formData.append('token', token);

    try {
      await firstValueFrom(
        this.http.post(environment.authority_short + 'auth.php', formData).pipe(
          catchError(error => {
            if (error.status === 409) {
              return throwError(() => new Error('Email already verified'));
            }
            return throwError(() => new Error('Email verification failed'));
          })
        )
      );
      // Handle success
    } catch (error) {
      // Handle error
      throw error;
    }
  }

  public async resendVerificationEmail(email: string): Promise<void> {
    const formData = new FormData();
    formData.append('mode', 'resendVerificationEmail');
    formData.append('email', email);

    try {
      await firstValueFrom(
        this.http.post(environment.authority_short + 'auth.php', formData).pipe(
          catchError(error => {
            return throwError(
              () => new Error('Resend verification email failed')
            );
          })
        )
      );
    } catch (error) {
      throw error;
    }
  }

  public async forgotPassword(email: string): Promise<void> {
    const formData = new FormData();
    formData.append('mode', 'forgotPassword');
    formData.append('email', email);

    try {
      await firstValueFrom(
        this.http.post(environment.authority_short + 'auth.php', formData).pipe(
          catchError(error => {
            if (error.status === 404) {
              return throwError(
                () => new Error('No user with this email not found')
              );
            }
            return throwError(() => new Error('Sending password reset failed'));
          })
        )
      );
      // Handle success
    } catch (error) {
      // Handle error
      throw error;
    }
  }

  public async resetPassword(
    passwordResetUser: PasswordResetModel
  ): Promise<void> {
    const formData = new FormData();
    formData.append('mode', 'resetPassword');
    formData.append('token', passwordResetUser.token);
    formData.append('password', passwordResetUser.password);

    try {
      await firstValueFrom(
        this.http.post(environment.authority_short + 'auth.php', formData).pipe(
          catchError(error => {
            if (error.status === 404) {
              return throwError(
                () => new Error('No user with this email not found')
              );
            }
            if (error.status === 409) {
              return throwError(() => new Error('Invalid token'));
            }
            return throwError(() => new Error('Password reset failed'));
          })
        )
      );
      // Handle success
    } catch (error) {
      // Handle error
      throw error;
    }
  }

  public logOut() {
    const formData = new FormData();
    formData.append('mode', 'logout');

    this.http
      .post(environment.authority_short + 'auth.php', formData)
      .subscribe();
    this.userService.setUser(null);
    this.userService.currentUser$.next(null);
    this.authCheck$.next(false);
  }

  public isLoggedIn() {
    return this.authCheck$.value;
  }
}
